import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import cn from "classnames";
import { Button, Skeleton } from "@/shared/ui";
import { ACCOUNTING_EQUITY_CHANGES_REPORT_PAGE_ROUTE } from "@/shared/utils";
import type { EquityChangesResponse, EquityMoney } from "@/features/accounting/financial-reports/types";
import { formatReportAmount, getEquityCardFigures } from "@/features/accounting/financial-reports/utils";

interface Props {
  data?: EquityChangesResponse;
  isLoading?: boolean;
  error?: unknown;
  className?: string;
  activeProjectId?: number | null;
}

function formatFigure(value: EquityMoney): string {
  if (value === null) return "—";
  return formatReportAmount(value).text;
}

export default function EquityChangesCard({ data, isLoading, error, className, activeProjectId }: Props) {
  const { t } = useTranslation("FinancialReportPage");
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <Skeleton width={300} height={24} className="mb-4" />
        <div className="space-y-4">
          <Skeleton width="100%" height={120} />
          <Skeleton width="100%" height={60} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <h3 className="text-body-bold-lg content-base-primary mb-4">{t("equityChanges.title")}</h3>
        <div className="text-body-regular-md content-action-neutral">
          {error ? t("equityChanges.error") : t("equityChanges.noData")}
        </div>
      </div>
    );
  }

  const figures = getEquityCardFigures(data.rows);
  const movementRows = [
    { key: "opening", label: t("equityChanges.figures.opening"), value: figures.opening },
    { key: "profit", label: t("equityChanges.figures.profit"), value: figures.profit },
    { key: "revaluation", label: t("equityChanges.figures.revaluation"), value: figures.revaluation },
  ];

  return (
    <div className={cn("py-5 px-6 radius-3xl surface-base-fill flex flex-col gap-5", className)}>
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center gap-5">
          <h3 className="text-body-bold-lg content-base-primary">{t("equityChanges.title")}</h3>
          <p className="text-label-sm content-base-low">
            {t("equityChanges.cardYear", { year: data.report_year })}
          </p>
        </div>
        {activeProjectId != null && (
          <p className="text-body-regular-sm content-base-low">{t("equityChanges.orgWideNote")}</p>
        )}
      </div>

      <div className="flex flex-col">
        {movementRows.map(function renderMovementRow(row) {
          return (
            <div key={row.key} className="py-3 border-t surface-container-stroke">
              <div className="flex justify-between items-center gap-5">
                <p className="text-body-regular-md content-action-neutral">{row.label}</p>
                <p className="text-body-bold-md content-base-primary text-right">{formatFigure(row.value)}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between items-center gap-5 p-4 radius-md surface-component-fill">
        <p className="text-body-regular-md content-base-primary">{t("equityChanges.figures.closing")}</p>
        <p className="text-heading-lg content-base-primary text-right">{formatFigure(figures.closing)}</p>
      </div>

      <Button
        variant="tertiary"
        size="lg"
        type="button"
        onClick={() => navigate(`${ACCOUNTING_EQUITY_CHANGES_REPORT_PAGE_ROUTE}?year=${data.report_year}`)}>
        {t("equityChanges.detailsButton")}
      </Button>
    </div>
  );
}
